/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link } from 'react-router';

import { Button, typography2 } from '@coursera/cds-core';
import type { PageTabName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

export type Props = {
  trackingName?: PageTabName;
  label?: string;
  href?: string;
  name?: string;
  isCurrent?: boolean;
  /**
   * Opt in to client-side routing. Only safe when the router of the app currently rendering the
   * header owns `href` — react-router otherwise pushes into an app with no matching route and
   * renders that app's NotFound instead of navigating. Defaults to a full page load.
   */
  enableClientSideNavigation?: boolean;
};

const styles = {
  link: css`
    &&& {
      &.isCurrent {
        color: var(--cds-color-interactive-primary-hover);
        ${typography2.actionSecondary};
      }

      ${typography2.bodySecondary}
      color: var(--cds-color-neutral-primary-weak);

      :hover,
      :focus,
      :active {
        background-color: var(--cds-color-interactive-background-primary-hover-weak);
        text-decoration: none;
        color: var(--cds-color-interactive-primary-hover);
      }
    }
  `,
};

const LeftNavButtonGridItem = ({ label, href, name, trackingName, isCurrent, enableClientSideNavigation }: Props) => {
  const track = useTracker();

  return (
    <Button
      href={href}
      to={enableClientSideNavigation ? href : undefined}
      variant="ghost"
      css={[styles.link]}
      data-testid={`nav-link-grid-item-${name ?? href}`}
      onClick={() => {
        track('click_page_tab', { pageTab: { name: trackingName } });
      }}
      component={Link}
      size="small"
      className={isCurrent ? 'isCurrent' : undefined}
      aria-current={isCurrent ? 'page' : undefined}
    >
      {label}
    </Button>
  );
};

export default LeftNavButtonGridItem;
